import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';

interface CsvImportDialogProps {
    open: boolean;
    onClose: () => void;
    courses: any[];
    onSuccess: () => void;
}

const CsvImportDialog: React.FC<CsvImportDialogProps> = ({ open, onClose, courses, onSuccess }) => {
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [importCourseId, setImportCourseId] = useState<number | ''>('');
    const [importResults, setImportResults] = useState<{ success: number; failed: number; errors: string[] } | null>(null);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();

    const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setCsvFile(file);
            setImportResults(null);
        }
    };

    const handleImportCsv = async () => {
        if (!csvFile || !importCourseId) {
            notify(translate('enrollments.csvImport.selectFileAndCourse'), { type: 'warning' });
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const text = e.target?.result as string;
                const lines = text.split('\n').filter(line => line.trim());
                const dataLines = lines.slice(1);

                const importCourse = courses.find((c: any) => c.id === importCourseId);
                let success = 0;
                let failed = 0;
                const errors: string[] = [];

                const { data: allUsers } = await dataProvider.getList('users', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'email', order: 'ASC' },
                    filter: {}
                });

                for (const line of dataLines) {
                    const email = line.trim().replace(/"/g, '').replace(/,/g, '');
                    if (!email) continue;

                    try {
                        const user = allUsers.find((u: any) => u.email === email);

                        if (!user) {
                            errors.push(translate('enrollments.csvImport.userNotFound') + ': ' + email);
                            failed++;
                            continue;
                        }

                        if (importCourse?.isLicensed) {
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/licensed-courses/${importCourseId}/enroll`, {
                                method: 'POST',
                                headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                    'Content-Type': 'application/json',
                                    'Accept': 'application/json'
                                },
                                body: JSON.stringify({ userId: user.id })
                            });
                            if (!response.ok) {
                                const body = await response.json().catch(() => ({}));
                                errors.push(email + ': ' + (body.detail || body.error || translate('enrollments.csvImport.enrollError')));
                                failed++;
                                continue;
                            }
                            success++;
                            continue;
                        }

                        await dataProvider.create('course_enrollments', {
                            data: {
                                userId: user.id,
                                userName: ((user.firstName || '') + ' ' + (user.lastName || '')).trim() || user.email,
                                userEmail: user.email,
                                course: '/api/courses/' + importCourseId,
                                status: 'enrolled',
                                progressPercentage: 0
                            }
                        });
                        success++;
                    } catch (error: any) {
                        errors.push(email + ': ' + (error.message || translate('enrollments.csvImport.enrollError')));
                        failed++;
                    }
                }

                setImportResults({ success, failed, errors });
                notify(translate('enrollments.csvImport.importComplete', { success, failed }), {
                    type: success > 0 ? 'success' : 'warning'
                });
                onSuccess();
            } catch (error) {
                notify(translate('enrollments.csvImport.processingError'), { type: 'error' });
            }
        };
        reader.readAsText(csvFile);
    };

    const handleCloseDialog = () => {
        setCsvFile(null);
        setImportCourseId('');
        setImportResults(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>{translate('enrollments.csvImport.title')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Alert severity="info">
                        {translate('enrollments.csvImport.formatInfo')}
                    </Alert>

                    <FormControl fullWidth required>
                        <InputLabel>{translate('enrollments.csvImport.course')}</InputLabel>
                        <Select
                            value={importCourseId}
                            label={translate('enrollments.csvImport.course')}
                            onChange={(e) => setImportCourseId(e.target.value as number)}
                        >
                            {courses.map((course) => (
                                <MenuItem key={course.id} value={course.id}>
                                    {course.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadIcon />}
                    >
                        {translate('enrollments.csvImport.selectFile')}
                        <input
                            type="file"
                            accept=".csv"
                            hidden
                            onChange={handleCsvFileChange}
                        />
                    </Button>

                    {csvFile && (
                        <Typography variant="body2" color="text.secondary">
                            {translate('enrollments.csvImport.file')}: {csvFile.name}
                        </Typography>
                    )}

                    {importResults && (
                        <Box>
                            <Alert severity={importResults.failed === 0 ? 'success' : 'warning'}>
                                <Typography variant="body2">
                                    {translate('enrollments.csvImport.results.success')}: {importResults.success} | {translate('enrollments.csvImport.results.failed')}: {importResults.failed}
                                </Typography>
                            </Alert>
                            {importResults.errors.length > 0 && (
                                <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                                    <Typography variant="caption" component="div">
                                        {translate('enrollments.csvImport.results.errors')}:
                                    </Typography>
                                    {importResults.errors.map((error, idx) => (
                                        <Typography key={idx} variant="caption" color="error" component="div">
                                            • {error}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog}>
                    {importResults ? translate('ra.action.close') : translate('ra.action.cancel')}
                </Button>
                {!importResults && (
                    <Button
                        onClick={handleImportCsv}
                        variant="contained"
                        disabled={!csvFile || !importCourseId}
                    >
                        {translate('enrollments.csvImport.import')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CsvImportDialog;
